import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { zodResolver } from "@hookform/resolvers/zod";
import { useGetUniversitiesQuery } from "@/shared/api/common";
import { ArrowRightIcon } from "@/shared/assets/icons";
import { FileUploader } from "@/shared/components";
import { useDocumentSuggest } from "@/shared/hooks";
import { Button, Input, SearchableSelect, Select, ToggleButton, Toast } from "@/shared/ui";
import { applyOcrFields, mapOcrDegree, resolveUniversityForOcr } from "@/shared/utils";
import { useCreateEducationMutation, useUpdateEducationMutation } from "./api";
import type { EducationRecord } from "./types";
import { createEducationSchema, type EducationFormValues } from "./validation";

const DEGREE_VALUES = ["bachelor", "master", "phd", "college", "secondary_general"];

interface Props {
  token: string;
  record?: EducationRecord;
  onCancel: () => void;
  onSuccess: () => void;
  onToast: (toast: { text: string; color: "positive" | "negative" | "notice" | "grey" }) => void;
}

function buildDefaults(record?: EducationRecord): EducationFormValues {
  return {
    degree: record?.degree ?? "",
    university_name: record?.university_name ?? "",
    specialty: record?.specialty ?? "",
    admission_year: record?.admission_year ?? "",
    graduation_year: record?.graduation_year ?? "",
    diploma_number: record?.diploma_number ?? "",
    is_incomplete: record?.is_incomplete ?? false,
    left_from_course: record?.left_from_course ?? "",
    diploma_file: null,
    diploma_transcript_file: null,
  };
}

export default function EducationForm({ token, record, onCancel, onSuccess, onToast }: Props) {
  const { t } = useTranslation("ApplyApplicationPage");
  const isEdit = !!record;

  const { data: universities = [] } = useGetUniversitiesQuery();
  const [createEducation, { isLoading: isCreating }] = useCreateEducationMutation();
  const [updateEducation, { isLoading: isUpdating }] = useUpdateEducationMutation();
  const isSaving = isCreating || isUpdating;

  const diplomaSatisfied = isEdit && !!record?.diploma_file_url;
  const transcriptSatisfied = isEdit && !!record?.diploma_transcript_file_url;

  const schema = useMemo(
    function buildSchema() {
      return createEducationSchema(diplomaSatisfied, transcriptSatisfied);
    },
    [diplomaSatisfied, transcriptSatisfied],
  );

  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    watch,
    formState: { errors },
  } = useForm<EducationFormValues>({
    resolver: zodResolver(schema),
    defaultValues: buildDefaults(record),
  });

  const [ocrHighlight, setOcrHighlight] = useState<Set<string>>(new Set());
  const [ocrToast, setOcrToast] = useState<{ color: "positive" | "notice"; text: string } | null>(null);
  const { scan: scanDocument, state: ocrState } = useDocumentSuggest(token);

  const degree = watch("degree");
  const universityName = watch("university_name");
  const isIncomplete = watch("is_incomplete");
  const diplomaFile = watch("diploma_file");
  const transcriptFile = watch("diploma_transcript_file");

  const degreeOptions = DEGREE_VALUES.map(function toOption(value) {
    return { value, label: t(`education.degree.options.${value}`) };
  });

  function isHighlighted(field: string): boolean {
    return ocrHighlight.has(field);
  }

  async function runDiplomaOcr(file: File) {
    setOcrToast(null);
    const result = await scanDocument(file, "diploma");
    if (result.status === "idle") return;
    const fields = result.fields ?? {};

    const matched =
      (mapOcrDegree(fields.degree) ? 1 : 0) +
      (resolveUniversityForOcr(fields.university_name, universities) ? 1 : 0) +
      (fields.specialty?.trim() ? 1 : 0) +
      (fields.graduation_year?.trim() ? 1 : 0) +
      (fields.diploma_number?.trim() ? 1 : 0);
    if (matched * 2 < 5) {
      setOcrToast({ color: "notice", text: "education.ocr.wrongFile" });
      return;
    }

    const filled: string[] = [];

    if (getValues("degree").trim() === "") {
      const degreeValue = mapOcrDegree(fields.degree);
      if (degreeValue) {
        setValue("degree", degreeValue, { shouldValidate: true });
        filled.push("degree");
      }
    }

    if (getValues("university_name").trim() === "") {
      const universityValue = resolveUniversityForOcr(fields.university_name, universities);
      if (universityValue) {
        setValue("university_name", universityValue, { shouldValidate: true });
        filled.push("university_name");
      }
    }

    const textKeys: ("specialty" | "graduation_year" | "diploma_number")[] = getValues("is_incomplete")
      ? ["specialty"]
      : ["specialty", "graduation_year", "diploma_number"];
    const textResult = applyOcrFields(
      {
        specialty: getValues("specialty"),
        graduation_year: getValues("graduation_year"),
        diploma_number: getValues("diploma_number"),
      },
      fields,
      textKeys,
    );
    for (const key of textKeys) {
      if (textResult.filled.includes(key)) {
        setValue(key, textResult.next[key], { shouldValidate: true });
        filled.push(key);
      }
    }

    if (filled.length > 0) {
      setOcrHighlight(new Set(filled));
      setOcrToast({ color: "positive", text: "education.ocr.filled" });
    }
  }

  function handleDiplomaChange(file: File | null) {
    setValue("diploma_file", file, { shouldValidate: true });
    if (file) void runDiplomaOcr(file);
    else setOcrToast(null);
  }

  function handleIncompleteChange(checked: boolean) {
    setValue("is_incomplete", checked, { shouldValidate: true });
    if (checked) {
      setValue("graduation_year", "", { shouldValidate: true });
      setValue("diploma_number", "", { shouldValidate: true });
      setValue("diploma_file", null, { shouldValidate: true });
      setValue("diploma_transcript_file", null, { shouldValidate: true });
    } else {
      setValue("left_from_course", "", { shouldValidate: true });
    }
  }

  async function onSubmit(values: EducationFormValues) {
    if (isSaving) return;

    const body = {
      degree: values.degree,
      university_name: values.university_name,
      specialty: values.specialty,
      admission_year: values.admission_year,
      is_incomplete: values.is_incomplete,
      graduation_year: values.is_incomplete ? "" : values.graduation_year,
      diploma_number: values.is_incomplete ? "" : values.diploma_number,
      left_from_course: values.is_incomplete ? values.left_from_course : "",
      diploma_file: values.is_incomplete ? null : values.diploma_file,
      diploma_transcript_file: values.is_incomplete ? null : values.diploma_transcript_file,
    };

    try {
      if (isEdit && record) {
        await updateEducation({ token, id: record.id, body }).unwrap();
        onToast({ color: "positive", text: "education.toast.update_success" });
      } else {
        await createEducation({ token, body }).unwrap();
        onToast({ color: "positive", text: "education.toast.success" });
      }
      onSuccess();
    } catch {
      onToast({ color: "negative", text: "education.toast.error" });
    }
  }

  return (
    <form className="flex flex-col gap-6" onSubmit={handleSubmit(onSubmit)}>
      {!isIncomplete && (
        <div className="flex flex-col gap-4">
          <p className="text-body-bold-md content-base-primary">{t("education.upload_title")}</p>

          <FileUploader
            label={t("education.diploma_file.label")}
            hasRequiredLabel
            value={diplomaFile}
            existingFileUrl={record?.diploma_file_url ?? null}
            onChange={handleDiplomaChange}
            maxSizeMB={50}
            error={errors.diploma_file?.message ? t(errors.diploma_file.message as string) : null}
          />

          {ocrState === "scanning" ? (
            <Toast color="grey" text={t("education.ocr.scanning")} closable={false} autoClose={false} isFullWidth />
          ) : ocrToast ? (
            <Toast
              key={`${ocrToast.color}-${ocrToast.text}`}
              color={ocrToast.color}
              text={t(ocrToast.text)}
              isFullWidth
              onClose={() => setOcrToast(null)}
            />
          ) : (
            <Toast color="grey" text={t("education.ocr.hint")} closable={false} autoClose={false} isFullWidth />
          )}

          <FileUploader
            label={t("education.diploma_transcript.label")}
            hasRequiredLabel
            value={transcriptFile}
            existingFileUrl={record?.diploma_transcript_file_url ?? null}
            onChange={(file) => setValue("diploma_transcript_file", file ?? null, { shouldValidate: true })}
            maxSizeMB={50}
            error={
              errors.diploma_transcript_file?.message ? t(errors.diploma_transcript_file.message as string) : null
            }
          />
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Select
          label={t("education.degree.label")}
          hasRequiredLabel
          options={degreeOptions}
          value={degree}
          onChange={(v) => setValue("degree", v ?? "", { shouldValidate: true })}
          placeholder={t("education.degree.placeholder")}
          highlight={isHighlighted("degree")}
          error={errors.degree?.message ? t(errors.degree.message) : undefined}
        />

        <SearchableSelect
          label={t("education.university.label")}
          hasRequiredLabel
          placeholder={t("education.university.placeholder")}
          options={universities}
          searchKeys={["name"]}
          displayKey="name"
          hideSearchIcon
          highlight={isHighlighted("university_name")}
          value={universities.find((u) => u.name === universityName) ?? null}
          onChange={(selected) => setValue("university_name", selected?.name ?? "", { shouldValidate: true })}
          error={errors.university_name?.message ? t(errors.university_name.message) : undefined}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input
          label={t("education.specialty.label")}
          hasRequiredLabel
          placeholder={t("education.specialty.placeholder")}
          highlight={isHighlighted("specialty")}
          {...register("specialty")}
          error={errors.specialty?.message ? t(errors.specialty.message) : undefined}
        />
        <Input
          label={t("education.admission_year.label")}
          hasRequiredLabel
          placeholder={t("education.admission_year.placeholder")}
          {...register("admission_year")}
          error={errors.admission_year?.message ? t(errors.admission_year.message) : undefined}
        />
      </div>

      <div className="flex items-center justify-between gap-4">
        <span className="text-label-sm content-base-secondary">{t("education.incomplete.label")}</span>
        <ToggleButton checked={isIncomplete} onChange={handleIncompleteChange} />
      </div>

      {isIncomplete ? (
        <Input
          label={t("education.left_from_course.label")}
          hasRequiredLabel
          maxLength={100}
          placeholder={t("education.left_from_course.placeholder")}
          {...register("left_from_course")}
          error={errors.left_from_course?.message ? t(errors.left_from_course.message as string) : undefined}
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label={t("education.year.label")}
            hasRequiredLabel
            placeholder={t("education.year.placeholder")}
            highlight={isHighlighted("graduation_year")}
            {...register("graduation_year")}
            error={errors.graduation_year?.message ? t(errors.graduation_year.message as string) : undefined}
          />
          <Input
            label={t("education.diploma_number.label")}
            hasRequiredLabel
            placeholder={t("education.diploma_number.placeholder")}
            highlight={isHighlighted("diploma_number")}
            {...register("diploma_number")}
            error={errors.diploma_number?.message ? t(errors.diploma_number.message as string) : undefined}
          />
        </div>
      )}

      <div className="flex justify-end pt-4 gap-3">
        <Button variant="secondary" size="lg" onClick={onCancel} disabled={isSaving} type="button">
          {t("education.cancel")}
        </Button>
        <Button variant="primary" size="lg" type="submit" disabled={isSaving || ocrState === "scanning"}>
          {isSaving ? t("education.saving") : isEdit ? t("education.save_changes") : t("education.save")}
          {!isSaving && (
            <span className="text-white">
              <ArrowRightIcon />
            </span>
          )}
        </Button>
      </div>
    </form>
  );
}
